// src/pages/patient/PatientRaiseTicket.jsx
//
// /patient/tickets/new -- raise a support ticket.
//
//   * ?category=&appointment_id=&invoice_id= preselect the form (the server
//     revalidates ownership on submit);
//   * attachments upload as soon as they are chosen, against this draft's
//     client_request_id; submit sends only their upload tokens;
//   * the client_request_id is generated ONCE per draft and reused on every
//     retry, so a retried submit never creates a second ticket;
//   * a failed submit keeps the whole draft and its files; success opens the
//     new ticket's conversation.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { apiErrorCode, readApiError } from "../../lib/apiErrors";
import { newClientRequestId } from "../../lib/clientRequestId";
import { browserTimezone, formatDateTimeInZone } from "../../lib/displayTimezone";
import useSequencedRequest from "../../lib/useSequencedRequest";
import { patientTicketsApi } from "../../services/patientTickets";
import { InlineError, classNames, money } from "../../components/patient/booking/bookingUi";
import { AttachmentPicker, FALLBACK_VOCAB, usePendingUploads } from "../../components/patient/tickets/ticketUi";

export const CATEGORY_CARDS = [
  { value: "appointment", label: "Appointment", description: "Reschedule, cancellation, confirmation, doctor delay, or visit issues" },
  { value: "invoice", label: "Invoice", description: "Payment failure, incorrect amount, refund, receipt, or invoice issue" },
  { value: "other", label: "Other", description: "General support questions that do not belong to appointment or invoice" },
];

export const CHECKLIST = [
  "Select the exact appointment or invoice",
  "Use Other subject only when predefined subjects do not fit",
  "Attach screenshots for payment or invoice errors",
];

const CATEGORY_VALUES = CATEGORY_CARDS.map((c) => c.value);
const MIN_CONTENT = 3;
const MAX_CONTENT = 10000;
const FIELD_ORDER = ["category", "appointment_id", "invoice_id", "subject_id", "subject_text", "content"];
const FIELD_IDS = {
  category: "ticket-category-appointment",
  appointment_id: "ticket-appointment",
  invoice_id: "ticket-invoice",
  subject_id: "ticket-subject",
  subject_text: "ticket-subject-text",
  priority: "ticket-priority",
  content: "ticket-content",
};

export function validateTicketDraft(form) {
  const errors = {};
  if (!CATEGORY_VALUES.includes(form.category)) errors.category = "Choose what this ticket is about.";
  if (form.category === "appointment" && !form.appointment_id) errors.appointment_id = "Select the appointment this is about.";
  if (form.category === "invoice" && !form.invoice_id) errors.invoice_id = "Select the invoice this is about.";
  if ((form.category === "appointment" || form.category === "invoice") && !form.subject_id) errors.subject_id = "Select a subject.";
  if (form.category === "other") {
    const text = form.subject_text.trim();
    if (!text) errors.subject_text = "Enter a subject.";
    else if (text.length < 3) errors.subject_text = "The subject must be at least 3 characters.";
  }
  const content = form.content.trim();
  if (!content) errors.content = "Describe the issue.";
  else if (content.length < MIN_CONTENT) errors.content = `Add a little more detail (at least ${MIN_CONTENT} characters).`;
  else if (content.length > MAX_CONTENT) errors.content = `Keep the description under ${MAX_CONTENT} characters.`;
  return errors;
}

function initialForm(params) {
  const invoiceId = params.get("invoice_id") || "";
  const appointmentId = params.get("appointment_id") || "";
  let category = params.get("category") || "";
  if (!CATEGORY_VALUES.includes(category)) category = invoiceId ? "invoice" : appointmentId ? "appointment" : "";
  return {
    category,
    appointment_id: category === "appointment" ? appointmentId : "",
    invoice_id: category === "invoice" ? invoiceId : "",
    subject_id: "",
    subject_text: "",
    priority: "",
    content: "",
  };
}

function FieldError({ id, message }) {
  if (!message) return null;
  return <p id={id} className="mt-1 text-xs font-semibold text-rose-700">{message}</p>;
}

function RoutingPanel() {
  return (
    <section aria-labelledby="routing-title" className="rounded-3xl border border-border bg-white p-5 shadow-soft">
      <h2 id="routing-title" className="text-base font-semibold text-text">Smart routing</h2>
      <ul className="mt-3 space-y-2 text-sm text-slate-700">
        <li><span className="font-semibold text-text">Appointment tickets</span> → the clinic that handles your appointment.</li>
        <li><span className="font-semibold text-text">Invoice tickets</span> → routed by subject to the clinic or PulseCare billing support.</li>
        <li><span className="font-semibold text-text">Other tickets</span> → PulseCare admin first, who will involve the right team.</li>
      </ul>
    </section>
  );
}

function Checklist() {
  return (
    <section aria-labelledby="checklist-title" className="rounded-3xl border border-border bg-white p-5 shadow-soft">
      <h2 id="checklist-title" className="text-base font-semibold text-text">Before submitting</h2>
      <ul className="mt-3 space-y-2 text-sm text-slate-700" data-testid="ticket-checklist">
        {CHECKLIST.map((item) => (
          <li key={item} className="flex gap-2">
            <span aria-hidden="true" className="text-primary">✓</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function PatientRaiseTicket() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [timeZone] = useState(() => browserTimezone() || "");
  const [form, setForm] = useState(() => initialForm(searchParams));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null); // { message, code }
  const [submitting, setSubmitting] = useState(false);
  const [referenceNotice, setReferenceNotice] = useState("");
  const submittingRef = useRef(false);
  // One idempotency key for this draft: kept across failed submits and retries.
  const clientRequestIdRef = useRef(null);
  if (!clientRequestIdRef.current) clientRequestIdRef.current = newClientRequestId();
  const preselectChecked = useRef(false);

  const options = useSequencedRequest(null);
  const references = useSequencedRequest(null);
  const runOptions = options.run;
  const runReferences = references.run;

  const loadOptions = useMemo(() => () => runOptions(({ signal }) => patientTicketsApi.filterOptions({ signal })), [runOptions]);
  const loadReferences = useMemo(() => () => runReferences(({ signal }) => patientTicketsApi.referenceOptions({ signal })), [runReferences]);
  useEffect(() => {
    loadOptions();
    loadReferences();
  }, [loadOptions, loadReferences]);

  const opt = options.data || {};
  const policy = opt.attachment_policy;
  const uploads = usePendingUploads((file) => patientTicketsApi.uploadDraftAttachment(clientRequestIdRef.current, file), policy);
  const priorities = opt.priorities?.length ? opt.priorities.filter((p) => p.value !== "urgent") : FALLBACK_VOCAB.priorities;
  const defaultPriority = opt.default_priority || "medium";
  const priority = form.priority || defaultPriority;
  const subjects = useMemo(() => {
    const list = opt.subjects?.[form.category];
    return Array.isArray(list) ? [...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)) : [];
  }, [opt.subjects, form.category]);
  const appointmentOptions = useMemo(() => references.data?.appointments || [], [references.data]);
  const invoiceOptions = useMemo(() => references.data?.invoices || [], [references.data]);
  const selectedSubject = subjects.find((s) => String(s.id) === String(form.subject_id)) || null;

  // A preselected reference that is not in the patient's own list is dropped
  // (the server would reject it anyway).
  useEffect(() => {
    if (preselectChecked.current || !references.data) return;
    preselectChecked.current = true;
    if (form.appointment_id && !appointmentOptions.some((a) => String(a.id) === String(form.appointment_id))) {
      setForm((prev) => ({ ...prev, appointment_id: "" }));
      setReferenceNotice("The linked appointment was not found in your account. Please select it from the list.");
    }
    if (form.invoice_id && !invoiceOptions.some((i) => String(i.id) === String(form.invoice_id))) {
      setForm((prev) => ({ ...prev, invoice_id: "" }));
      setReferenceNotice("The linked invoice was not found in your account. Please select it from the list.");
    }
  }, [references.data, appointmentOptions, invoiceOptions, form.appointment_id, form.invoice_id]);

  function update(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
    setSubmitError(null);
  }

  function chooseCategory(value) {
    if (value === form.category) return;
    setForm((prev) => ({ ...prev, category: value, subject_id: "", subject_text: prev.subject_text, appointment_id: value === "appointment" ? prev.appointment_id : "", invoice_id: value === "invoice" ? prev.invoice_id : "" }));
    setErrors({});
    setSubmitError(null);
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (submittingRef.current) return;
    const found = validateTicketDraft(form);
    const clean = Object.fromEntries(Object.entries(found).filter(([, v]) => v));
    setErrors(clean);
    if (Object.keys(clean).length) {
      const first = FIELD_ORDER.find((key) => clean[key]);
      document.getElementById(FIELD_IDS[first])?.focus();
      return;
    }
    if (uploads.busy) {
      setSubmitError({ message: "Please wait for your files to finish uploading." });
      return;
    }
    if (uploads.failed) {
      setSubmitError({ message: "One of your files did not upload. Retry or remove it before submitting." });
      return;
    }

    const payload = {
      client_request_id: clientRequestIdRef.current,
      category: form.category,
      priority,
      content: form.content.trim(),
      upload_tokens: uploads.tokens,
    };
    if (form.category === "appointment") {
      payload.appointment_id = form.appointment_id;
      payload.subject_id = form.subject_id;
    } else if (form.category === "invoice") {
      payload.invoice_id = form.invoice_id;
      payload.subject_id = form.subject_id;
    } else {
      payload.subject_text = form.subject_text.trim();
    }

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await patientTicketsApi.create(payload);
      if (!created?.id) throw new Error("missing ticket id");
      navigate(`/patient/tickets/${encodeURIComponent(created.id)}`, { replace: true });
    } catch (err) {
      const code = apiErrorCode(err);
      let message = readApiError(err, "Your ticket could not be submitted. Your draft is kept — please try again.");
      if (code === "FEATURE_NOT_IN_PLAN") {
        message = `${readApiError(err, "Support tickets for this clinic are not available.")} You can choose “Other” to contact PulseCare support instead.`;
      }
      const field = err?.response?.data?.detail?.field;
      if (field && FIELD_IDS[field]) setErrors((prev) => ({ ...prev, [field]: readApiError(err) }));
      setSubmitError({ message, code });
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  const describedBy = (key, extra) => [errors[key] ? `${FIELD_IDS[key]}-error` : null, extra].filter(Boolean).join(" ") || undefined;
  const inputClass = (key) =>
    classNames("mt-1 w-full rounded-2xl border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30", errors[key] ? "border-rose-400" : "border-border");

  return (
    <div className="mx-auto w-full max-w-6xl py-2 sm:py-4">
      <Link to="/patient/tickets" className="text-sm font-semibold text-primary hover:underline">← My tickets</Link>
      <div className="mt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">ThePulseCare support</p>
        <h1 className="mt-1 text-2xl font-semibold text-text">Raise a ticket</h1>
        <p className="mt-1 text-sm text-muted">Select the right reference, explain the issue, and we will route it to the correct team</p>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <form onSubmit={onSubmit} noValidate aria-label="Raise a ticket" className="space-y-5 rounded-3xl border border-border bg-white p-5 shadow-soft lg:col-span-2">
          {options.error ? <InlineError message={readApiError(options.error, "Ticket options could not be loaded.")} onRetry={loadOptions} /> : null}

          <fieldset aria-describedby={errors.category ? "ticket-category-error" : undefined}>
            <legend className="text-sm font-semibold text-text">Category <span className="text-rose-600" aria-hidden="true">*</span></legend>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Category" aria-invalid={errors.category ? "true" : undefined}>
              {CATEGORY_CARDS.map((card) => {
                const selected = form.category === card.value;
                return (
                  <button key={card.value} type="button" role="radio" aria-checked={selected} id={`ticket-category-${card.value}`} onClick={() => chooseCategory(card.value)}
                    data-testid={`category-card-${card.value}`}
                    className={classNames("rounded-2xl border-2 p-4 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", selected ? "border-primary bg-primary/5" : "border-border bg-white hover:border-primary/40")}>
                    <div className="text-sm font-semibold text-text">{card.label}</div>
                    <div className="mt-1 text-xs text-muted">{card.description}</div>
                  </button>
                );
              })}
            </div>
            <FieldError id="ticket-category-error" message={errors.category} />
          </fieldset>

          {referenceNotice ? <p role="status" className="rounded-2xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{referenceNotice}</p> : null}
          {references.error ? <InlineError message={readApiError(references.error, "Your appointments and invoices could not be loaded.")} onRetry={loadReferences} /> : null}

          {form.category === "appointment" ? (
            <div>
              <label htmlFor="ticket-appointment" className="text-sm font-semibold text-text">Appointment <span className="text-rose-600" aria-hidden="true">*</span></label>
              <select id="ticket-appointment" value={form.appointment_id} onChange={(e) => update("appointment_id", e.target.value)} required
                aria-invalid={errors.appointment_id ? "true" : undefined} aria-describedby={describedBy("appointment_id")} className={inputClass("appointment_id")}>
                <option value="">{references.loading && !references.data ? "Loading appointments…" : "Select appointment"}</option>
                {appointmentOptions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label || [a.meta?.start_at ? formatDateTimeInZone(a.meta.start_at, timeZone) : null, a.meta?.service_name].filter(Boolean).join(" · ")}
                  </option>
                ))}
              </select>
              <FieldError id="ticket-appointment-error" message={errors.appointment_id} />
            </div>
          ) : null}

          {form.category === "invoice" ? (
            <div>
              <label htmlFor="ticket-invoice" className="text-sm font-semibold text-text">Invoice <span className="text-rose-600" aria-hidden="true">*</span></label>
              <select id="ticket-invoice" value={form.invoice_id} onChange={(e) => update("invoice_id", e.target.value)} required
                aria-invalid={errors.invoice_id ? "true" : undefined} aria-describedby={describedBy("invoice_id")} className={inputClass("invoice_id")}>
                <option value="">{references.loading && !references.data ? "Loading invoices…" : "Select invoice"}</option>
                {invoiceOptions.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.label || [i.meta?.invoice_number, money(i.meta?.total_amount, i.meta?.currency)].filter(Boolean).join(" · ")}
                  </option>
                ))}
              </select>
              <FieldError id="ticket-invoice-error" message={errors.invoice_id} />
            </div>
          ) : null}

          {form.category === "appointment" || form.category === "invoice" ? (
            <div>
              <label htmlFor="ticket-subject" className="text-sm font-semibold text-text">Subject <span className="text-rose-600" aria-hidden="true">*</span></label>
              <select id="ticket-subject" value={form.subject_id} onChange={(e) => update("subject_id", e.target.value)} required
                aria-invalid={errors.subject_id ? "true" : undefined} aria-describedby={describedBy("subject_id", selectedSubject?.description ? "ticket-subject-help" : null)} className={inputClass("subject_id")}>
                <option value="">Select subject</option>
                {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {selectedSubject?.description ? <p id="ticket-subject-help" className="mt-1 text-xs text-muted">{selectedSubject.description}</p> : null}
              <FieldError id="ticket-subject-error" message={errors.subject_id} />
            </div>
          ) : null}

          {form.category === "other" ? (
            <div>
              <label htmlFor="ticket-subject-text" className="text-sm font-semibold text-text">Subject <span className="text-rose-600" aria-hidden="true">*</span></label>
              <input id="ticket-subject-text" type="text" value={form.subject_text} maxLength={200} onChange={(e) => update("subject_text", e.target.value)} required
                placeholder="What is your question about?" aria-invalid={errors.subject_text ? "true" : undefined} aria-describedby={describedBy("subject_text")} className={inputClass("subject_text")} />
              <FieldError id="ticket-subject-text-error" message={errors.subject_text} />
            </div>
          ) : null}

          <div>
            <label htmlFor="ticket-priority" className="text-sm font-semibold text-text">Priority</label>
            <select id="ticket-priority" value={priority} onChange={(e) => update("priority", e.target.value)} className={inputClass("priority")}>
              {priorities.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          <div>
            <label htmlFor="ticket-content" className="text-sm font-semibold text-text">Describe the issue <span className="text-rose-600" aria-hidden="true">*</span></label>
            <textarea id="ticket-content" rows={6} value={form.content} maxLength={MAX_CONTENT} onChange={(e) => update("content", e.target.value)} required
              placeholder="Please explain what happened, when it happened, and what help you need..."
              aria-invalid={errors.content ? "true" : undefined} aria-describedby={describedBy("content")} className={inputClass("content")} />
            <FieldError id="ticket-content-error" message={errors.content} />
          </div>

          <AttachmentPicker uploads={uploads} disabled={submitting} policy={policy} idPrefix="ticket-new" title="Attachments" hint="Add screenshots or documents that help explain the issue." />

          {submitError ? (
            <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="ticket-submit-error">
              <p>{submitError.message}</p>
              {submitError.code === "FEATURE_NOT_IN_PLAN" && form.category !== "other" ? (
                <button type="button" onClick={() => chooseCategory("other")} className="mt-2 rounded-xl border border-rose-300 bg-white px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-100">
                  Switch to Other
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-wrap justify-end gap-2">
            <Link to="/patient/tickets" className="rounded-2xl border border-border bg-white px-4 py-2.5 text-sm font-semibold text-text hover:bg-slate-50">Cancel</Link>
            <button type="submit" disabled={submitting || uploads.busy} aria-busy={submitting || undefined}
              className="btn-primary-gradient rounded-2xl px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
              {submitting ? "Submitting…" : uploads.busy ? "Uploading files…" : "Submit ticket"}
            </button>
          </div>
        </form>

        <aside className="space-y-4">
          <RoutingPanel />
          <Checklist />
        </aside>
      </div>
    </div>
  );
}
